import React, { useState, useEffect, useCallback } from "react";
import { CssVarsProvider } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from "react-router-dom";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import CircularProgress from "@mui/joy/CircularProgress";
import Avatar from "@mui/joy/Avatar";
import Chip from "@mui/joy/Chip";
import Divider from "@mui/joy/Divider";
import SendIcon from "@mui/icons-material/Send";
import IconButton from "@mui/joy/IconButton";
import Textarea from "@mui/joy/Textarea";
import { gql, useQuery, useMutation } from "@apollo/client";

import Breadcrumbs from "../components/Breadcrumbs";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

const SUPPORT_TICKET_QUERY = gql`
  query SupportTicketById($id: ID!) {
    supportTicketById(id: $id) {
      id
      title
      description
      status
      createdAt
      submitter {
        id
        firstName
        lastName
      }
      messages {
        id
        content
        createdAt
        sender {
          id
          firstName
          lastName
        }
        receiver {
          id
          firstName
          lastName
        }
        read
      }
    }
  }
`;

const CREATE_MESSAGE = gql`
  mutation CreateMessage($supportTicketId: ID!, $content: String!) {
    createMessage(
      input: { supportTicketId: $supportTicketId, content: $content }
    ) {
      message {
        id
        content
        createdAt
        sender {
          id
          firstName
          lastName
        }
        receiver {
          id
          firstName
          lastName
        }
        read
      }
    }
  }
`;

const UNREAD_MESSAGES_COUNT = gql`
  query UnreadMessagesCountById($supportTicketId: ID!) {
    unreadMessagesCountById(supportTicketId: $supportTicketId)
  }
`;

const MARK_MESSAGES_AS_READ = gql`
  mutation MarkMessagesAsReadById($supportTicketId: ID!) {
    markMessagesAsReadById(input: { supportTicketId: $supportTicketId }) {
      success
      updatedCount
    }
  }
`;

// Chat content component
const ChatContent = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const supportTicketId = searchParams.get("support_ticket_id") || "";
  const [message, setMessage] = useState("");
  const [localMessages, setLocalMessages] = useState([]);
  const [lastSentMessageId, setLastSentMessageId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);

  // Sound notification function
  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio("/sounds/notification.mp3");
      audio.volume = 1.0; // Adjust volume (0.0 to 1.0)
      audio.play().catch((error) => {
        console.log("Could not play notification sound:", error);
      });
    } catch (error) {
      console.log("Error creating audio:", error);
    }
  }, []);

  const { data, loading, error, networkStatus } = useQuery(
    SUPPORT_TICKET_QUERY,
    {
      variables: { id: supportTicketId },
      skip: !supportTicketId || !token,
      notifyOnNetworkStatusChange: true,
    }
  );

  // Extract only the IDs we need (to avoid depending on entire object)
  const submitterId = data?.supportTicketById?.submitter?.id;
  const assigneeId = data?.supportTicketById?.assignee?.id;
  const ticketId = data?.supportTicketById?.id;

  // Query for unread messages count
  const { data: unreadData, refetch: refetchUnreadCount } = useQuery(
    UNREAD_MESSAGES_COUNT,
    {
      variables: { supportTicketId: supportTicketId },
      skip: !supportTicketId || !token,
      pollInterval: isPageVisible ? 0 : 3000, // Poll every 3 seconds when page is not visible, no polling when visible
      notifyOnNetworkStatusChange: true,
    }
  );

  // Mark messages as read mutation
  const [markMessagesAsRead] = useMutation(MARK_MESSAGES_AS_READ);

  // Initialize local messages from query data
  useEffect(() => {
    if (data?.supportTicketById?.messages) {
      setLocalMessages(data.supportTicketById.messages);
    }
  }, [data?.supportTicketById?.messages]);

  // Update unread count from query data
  useEffect(() => {
    if (unreadData?.unreadMessagesCountById !== undefined) {
      setUnreadCount(unreadData.unreadMessagesCountById);
    }
  }, [unreadData?.unreadMessagesCountById]);

  // Play sound notification when unread count increases and page is not visible
  useEffect(() => {
    if (unreadCount > previousUnreadCount && !isPageVisible) {
      playNotificationSound();
    }
    setPreviousUnreadCount(unreadCount);
  }, [unreadCount, previousUnreadCount, isPageVisible, playNotificationSound]);

  // Track page visibility and update title accordingly
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isTabActive = !document.hidden;
      setIsPageVisible(isTabActive);

      // Update title based on tab visibility and unread count
      if (!isTabActive && unreadCount > 0) {
        // Tab is inactive and there are unread messages - show count
        const originalTitle = document.title.replace(/^\(\d+\)\s*/, ""); // Remove existing count
        document.title = `(${unreadCount}) ${originalTitle}`;
      } else if (isTabActive) {
        // Tab is active - clear count from title
        const originalTitle = document.title.replace(/^\(\d+\)\s*/, "");
        document.title = originalTitle;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [unreadCount]);

  // Update page title when unread count changes (for when tab is already inactive)
  useEffect(() => {
    if (!isPageVisible && unreadCount > 0) {
      const originalTitle = document.title.replace(/^\(\d+\)\s*/, "");
      document.title = `(${unreadCount}) ${originalTitle}`;
    }
  }, [unreadCount, isPageVisible]);

  // Mark messages as read when page becomes visible
  useEffect(() => {
    if (isPageVisible && supportTicketId && unreadCount > 0) {
      markMessagesAsRead({
        variables: { supportTicketId: supportTicketId },
      })
        .then(() => {
          refetchUnreadCount();
        })
        .catch(console.error);
    }
  }, [
    isPageVisible,
    supportTicketId,
    unreadCount,
    markMessagesAsRead,
    refetchUnreadCount,
  ]);

  // Subscribe to WebSocket updates for this ticket
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    if (!ticketId) return;

    channel.subscribe(
      { channel: "SupportTicketChannel" },
      {
        received: (wsData) => {
          console.log("📨 Received WebSocket data:", wsData);

          // Filter by ticket ID
          if (
            wsData.type === "new_message" &&
            wsData.support_ticket_id &&
            String(wsData.support_ticket_id) === String(ticketId)
          ) {
            // Skip if this is the message we just sent (by ID)
            if (lastSentMessageId && wsData.message.id === lastSentMessageId) {
              console.log("⏭️ Skipping own message (by ID)");
              return;
            }

            // Skip if this is our own message (already added via mutation)
            // On chat page, the submitter is the one sending messages
            if (
              submitterId &&
              wsData.message.sender &&
              String(wsData.message.sender.id) === String(submitterId)
            ) {
              console.log("⏭️ Skipping own message (by profile ID)");
              return;
            }

            console.log("✅ Adding message to chat:", wsData.message);
            setLocalMessages((prevMessages) => {
              // Check if message already exists to avoid duplicates
              const exists = prevMessages.some(
                (msg) => msg.id === wsData.message.id
              );
              if (exists) {
                console.log("⏭️ Message already exists, skipping");
                return prevMessages;
              }
              return [...prevMessages, wsData.message];
            });

            // Update unread count if this is a message from support team to submitter
            if (
              wsData.message.receiver &&
              wsData.message.receiver.id === submitterId
            ) {
              // Immediately update unread count locally for instant UI update
              setUnreadCount((prevCount) => prevCount + 1);

              // Play sound notification if page is not visible
              if (!isPageVisible) {
                playNotificationSound();
              }

              // Then refetch to get the accurate count from server
              refetchUnreadCount();
            }
          }
        },
        connected: () => {
          console.log("✅ Connected to SupportTicketChannel");
        },
        disconnected: () => {
          console.log("❌ Disconnected from SupportTicketChannel");
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [ticketId, lastSentMessageId, submitterId]);

  const [createMessage, { loading: sendingMessage }] = useMutation(
    CREATE_MESSAGE,
    {
      onCompleted: (data) => {
        if (data?.createMessage?.message) {
          // Add the message optimistically to local state
          const newMessage = data.createMessage.message;

          // Store the message ID so we can skip it when it comes via WebSocket
          setLastSentMessageId(newMessage.id);

          setLocalMessages((prevMessages) => {
            // Check if message already exists to avoid duplicates
            const exists = prevMessages.some((msg) => msg.id === newMessage.id);
            if (exists) {
              return prevMessages;
            }
            return [...prevMessages, newMessage];
          });
        }
        setMessage("");
        // Scroll to bottom after sending message
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      },
      onError: (error) => {
        console.error("Error sending message:", error);
        // You could add a toast notification here
      },
    }
  );

  const messagesEndRef = React.useRef(null);

  // Check if error is 401 Unauthorized
  const is401Error =
    (error?.networkError as any)?.statusCode === 401 ||
    error?.graphQLErrors?.some(
      (e: any) => e.extensions?.code === "UNAUTHENTICATED"
    );

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (localMessages && localMessages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [localMessages]);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDisplayName = (sender: any) => {
    if (!sender) return "Support Team";
    return (
      `${sender.firstName || ""} ${sender.lastName || ""}`.trim() || "Unknown"
    );
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      console.log("No message to send");
      return;
    }

    if (!data?.supportTicketById?.id) {
      console.log("No support ticket data available yet");
      return;
    }

    if (!supportTicketId) {
      console.log("No support ticket ID available");
      return;
    }

    console.log("Sending message:", message.trim());

    try {
      await createMessage({
        variables: {
          supportTicketId: supportTicketId,
          content: message.trim(),
        },
      });
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleKeyPress = (event: any) => {
    if (event.key === "Enter" && !event.shiftKey && !sendingMessage) {
      event.preventDefault();
      if (message.trim()) {
        handleSendMessage();
      }
    }
  };

  const renderContent = () => {
    if (!token || !supportTicketId) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography level="h3" color="neutral">
            Invalid Link
          </Typography>
          <Typography textAlign="center">
            Please provide a valid token and support ticket ID in the URL
            parameters (?token=xxx&support_ticket_id=123)
          </Typography>
        </Box>
      );
    }

    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress size="lg" />
        </Box>
      );
    }

    if (error) {
      const errorTitle = is401Error ? "Unauthorized" : "Error";
      const errorMessage = is401Error
        ? "Access denied. This link is invalid or has expired."
        : error.message ||
          "The link may have expired or the ticket has been resolved.";

      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography level="h3" color="danger">
            {errorTitle}
          </Typography>
          <Typography color="danger" textAlign="center">
            {errorMessage}
          </Typography>
          {is401Error && (
            <Typography level="body-sm" sx={{ color: "text.secondary", mt: 1 }}>
              Please request a new link from the support team.
            </Typography>
          )}
        </Box>
      );
    }

    const ticket = data?.supportTicketById;

    if (!ticket) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography level="h3" color="danger">
            Error
          </Typography>
          <Typography textAlign="center">
            The link may have expired or the ticket has been resolved.
          </Typography>
        </Box>
      );
    }

    // Check if ticket is resolved
    if (ticket.status === "RESOLVED") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            flexDirection: "column",
            gap: 3,
            p: 4,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "success.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Typography level="h1" sx={{ color: "success.500" }}>
              ✓
            </Typography>
          </Box>

          <Typography
            level="h2"
            sx={{ color: "success.500", textAlign: "center" }}
          >
            Support Ticket Resolved
          </Typography>

          <Typography
            level="body-lg"
            sx={{ textAlign: "center", color: "text.secondary", maxWidth: 500 }}
          >
            This support ticket has been successfully resolved. The conversation
            is now closed.
          </Typography>

          <Box
            sx={{
              p: 3,
              backgroundColor: "background.level1",
              borderRadius: "md",
              border: "1px solid",
              borderColor: "divider",
              maxWidth: 600,
              width: "100%",
            }}
          >
            <Typography level="h4" sx={{ mb: 2, color: "text.primary" }}>
              Ticket Details
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  Ticket ID:
                </Typography>
                <Typography level="body-sm" sx={{ fontWeight: "md" }}>
                  #{ticket.id.slice(0, 8)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  Title:
                </Typography>
                <Typography
                  level="body-sm"
                  sx={{ fontWeight: "md", textAlign: "right", maxWidth: 300 }}
                >
                  {ticket.title}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  Status:
                </Typography>
                <Typography
                  level="body-sm"
                  sx={{ fontWeight: "md", color: "success.500" }}
                >
                  {ticket.status}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  Created:
                </Typography>
                <Typography level="body-sm" sx={{ fontWeight: "md" }}>
                  {formatDateTime(ticket.createdAt)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Typography
            level="body-sm"
            sx={{ textAlign: "center", color: "text.tertiary", mt: 2 }}
          >
            If you need further assistance, please create a new support ticket.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "calc(100vh - 120px)",
        }}
      >
        {/* Ticket Info */}
        <Sheet
          variant="soft"
          sx={{
            borderRadius: "sm",
            p: 2.5,
            mb: 2,
            backgroundColor: "#003366",
          }}
        >
          {/* First Row - Title */}
          <Typography
            level="title-lg"
            fontWeight="lg"
            sx={{ mb: 1.5, color: "#FFC107" }}
          >
            {ticket.title}
          </Typography>

          {/* Second Row - Metadata */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              alignItems: "center",
              flexWrap: "wrap",
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography level="body-xs" sx={{ color: "#FFC107" }}>
                Ticket ID:
              </Typography>
              <Typography
                level="body-sm"
                fontWeight="md"
                sx={{ color: "white" }}
              >
                #{ticket.id.slice(0, 8)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography level="body-xs" sx={{ color: "#FFC107" }}>
                Status:
              </Typography>
              <Chip
                variant="soft"
                size="sm"
                color={ticket.status === "PENDING" ? "warning" : "success"}
              >
                {ticket.status}
              </Chip>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography level="body-xs" sx={{ color: "#FFC107" }}>
                Created:
              </Typography>
              <Typography level="body-sm" sx={{ color: "white" }}>
                {formatDateTime(ticket.createdAt)}
              </Typography>
            </Box>
          </Box>

          {/* Third Row - Description */}
          <Box>
            <Typography level="body-sm" sx={{ color: "#FFC107" }}>
              {ticket.description}
            </Typography>
          </Box>
        </Sheet>

        {/* Messages Container */}
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: "sm",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0, // Important for flex child to shrink
          }}
        >
          {/* Scrollable Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minHeight: 0, // Important for scrollable content
            }}
          >
            {!localMessages || localMessages.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography level="body-md" sx={{ color: "text.secondary" }}>
                  No messages yet. The support team will respond to your ticket
                  soon.
                </Typography>
              </Box>
            ) : (
              localMessages.map((message: any, index: number) => {
                // Check if message sender is the ticket submitter (right side)
                // Otherwise it's support team (left side)
                const isSubmitterMessage =
                  message.sender &&
                  ticket.submitter &&
                  message.sender.id === ticket.submitter.id;

                return (
                  <Box
                    key={message.id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isSubmitterMessage
                        ? "flex-end"
                        : "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      {!isSubmitterMessage && (
                        <Avatar size="sm" sx={{ width: 24, height: 24 }}>
                          {getDisplayName(message.sender)
                            .charAt(0)
                            .toUpperCase()}
                        </Avatar>
                      )}
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.secondary", fontWeight: "md" }}
                      >
                        {getDisplayName(message.sender)}
                      </Typography>
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.tertiary" }}
                      >
                        • {formatDateTime(message.createdAt)}
                      </Typography>
                      {isSubmitterMessage && (
                        <Avatar size="sm" sx={{ width: 24, height: 24 }}>
                          {getDisplayName(message.sender)
                            .charAt(0)
                            .toUpperCase()}
                        </Avatar>
                      )}
                    </Box>
                    <Sheet
                      variant="soft"
                      color={isSubmitterMessage ? "primary" : "neutral"}
                      sx={{
                        p: 2,
                        borderRadius: "lg",
                        maxWidth: "70%",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        backgroundColor: isSubmitterMessage
                          ? "#003366"
                          : "#E3EFFB",
                      }}
                    >
                      <Typography
                        level="body-sm"
                        sx={{
                          color: isSubmitterMessage ? "#FFC107" : "#003366",
                        }}
                      >
                        {message.content}
                      </Typography>
                    </Sheet>
                  </Box>
                );
              })
            )}
            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </Box>

          {/* Message Input Area - Fixed at bottom */}
          <Divider />
          <Box
            sx={{
              p: 1.5,
              bgcolor: "background.level1",
              display: "flex",
              gap: 1,
              alignItems: "flex-end",
              flexShrink: 0, // Prevent shrinking
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Textarea
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              minRows={1}
              maxRows={4}
              disabled={sendingMessage}
              sx={{
                flex: 1,
                resize: "none",
              }}
            />
            <IconButton
              color="003366"
              disabled={
                !message.trim() ||
                sendingMessage ||
                loading ||
                !data?.supportTicketById?.id
              }
              onClick={handleSendMessage}
              sx={{
                minWidth: 40,
                minHeight: 40,
                flexShrink: 0,
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Sheet>
      </Box>
    );
  };

  return renderContent();
};

// Main Chat component with layout
export default function Chat() {
  return (
    <CssVarsProvider disableTransitionOnChange>
      <CssBaseline />
      <Router>
        <Box
          component="main"
          className="MainContent"
          sx={{
            px: { xs: 2, md: 6 },
            pt: 2,
            pb: { xs: 2, sm: 2, md: 3 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            width: "100%",
            height: "100vh",
            overflow: "auto",
            scrollbarGutter: "stable",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Breadcrumbs />
          </Box>
          <Routes>
            <Route path="/chat" element={<ChatContent />} />
            <Route path="*" element={<Navigate to="/chat" replace />} />
          </Routes>
        </Box>
      </Router>
    </CssVarsProvider>
  );
}
